<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import InboxesAPI from 'dashboard/api/inboxes';
import {
  HYBRID_QR_SOURCE,
  HYBRID_QR_STATE,
  hybridQrConnection,
} from 'dashboard/helper/whatsappHybrid';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import WhatsappLinkDeviceModal from '../components/WhatsappLinkDeviceModal.vue';
import HybridQrManualForm from './components/HybridQrManualForm.vue';
import HybridQrStatusBadge from './components/HybridQrStatusBadge.vue';

const props = defineProps({ inbox: { type: Object, required: true } });
const OPTION = {
  NATIVE: 'native',
  MANAGED: 'managed',
  LINKED: 'linked',
  MANUAL: 'manual',
};
const OFFICIAL_PROVIDER_NAMES = {
  whatsapp_cloud: 'WhatsApp Cloud',
  ycloud: 'YCloud',
};

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountId } = useAccount();

const qr = computed(() =>
  hybridQrConnection(props.inbox, store.getters['inboxes/getInboxes'] || [])
);
const officialProvider = computed(
  () => OFFICIAL_PROVIDER_NAMES[props.inbox.provider] || props.inbox.provider
);

const busy = ref(false);
const error = ref('');
const managedAvailable = ref(false);
const candidates = ref([]);
const nativeInstances = ref([]);
const selectedNative = ref(0);
const showAdvanced = ref(false);
const selectedOption = ref(OPTION.MANUAL);
const selectedCandidate = ref('');
const baseUrl = ref(props.inbox.attendance_connection?.base_url || '');
const token = ref('');
const changingMethod = ref(false);
const showLinkDeviceModal = ref(false);
const confirmDialog = ref(null);
let disposed = false;

const hybridAttendanceEnabled = ref(false);
const isSavingHybridAttendance = ref(false);
watch(
  () => props.inbox.hybrid_attendance_enabled,
  value => {
    hybridAttendanceEnabled.value = value === true;
  },
  { immediate: true }
);

const isNative = computed(() => qr.value.source === HYBRID_QR_SOURCE.NATIVE);
const isManagedOrManual = computed(() =>
  [
    HYBRID_QR_SOURCE.MANAGED,
    HYBRID_QR_SOURCE.MANUAL,
    HYBRID_QR_SOURCE.NATIVE,
  ].includes(qr.value.source)
);
const showSelector = computed(() => !qr.value.source || changingMethod.value);
// Only what works today is offered: the managed connection needs a provider registered for the account,
// and linking needs a QR inbox on the same number.
const nativeInstance = computed(
  () => nativeInstances.value[selectedNative.value]
);
const nativeStateLabel = instance =>
  instance.connection === 'open'
    ? t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_NATIVE.OPEN')
    : t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_NATIVE.CLOSED');
const nativeLabel = instance =>
  t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_NATIVE.ACTIVE', {
    name: instance.instance_name,
    last4: instance.phone_last4,
    state: nativeStateLabel(instance),
  });
const manualOnly = computed(
  () =>
    !nativeInstances.value.length &&
    !managedAvailable.value &&
    !candidates.value.length
);
const advancedVisible = computed(() => showAdvanced.value || manualOnly.value);
const options = computed(() =>
  [
    nativeInstance.value && {
      id: OPTION.NATIVE,
      label: t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_NATIVE.LABEL'),
      description: nativeLabel(nativeInstance.value),
    },
    managedAvailable.value && {
      id: OPTION.MANAGED,
      label: t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_MANAGED.LABEL'),
      description: t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_MANAGED.DESCRIPTION'),
    },
    candidates.value.length && {
      id: OPTION.LINKED,
      label: t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_LINKED.LABEL'),
      description: t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_LINKED.DESCRIPTION'),
    },
    advancedVisible.value && {
      id: OPTION.MANUAL,
      label: t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_MANUAL.LABEL'),
      description: t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_MANUAL.DESCRIPTION'),
    },
  ].filter(Boolean)
);
const confirmText = computed(() =>
  isNative.value
    ? {
        title: t('INBOX_MGMT.EDIT.HYBRID_QR.CONFIRM_UNLINK_NATIVE.TITLE'),
        description: t(
          'INBOX_MGMT.EDIT.HYBRID_QR.CONFIRM_UNLINK_NATIVE.DESCRIPTION'
        ),
        confirm: t('INBOX_MGMT.EDIT.HYBRID_QR.CONFIRM_UNLINK_NATIVE.CONFIRM'),
      }
    : {
        title: t('INBOX_MGMT.EDIT.HYBRID_QR.CONFIRM_DISCONNECT.TITLE'),
        description: t(
          'INBOX_MGMT.EDIT.HYBRID_QR.CONFIRM_DISCONNECT.DESCRIPTION'
        ),
        confirm: t('INBOX_MGMT.EDIT.HYBRID_QR.CONFIRM_DISCONNECT.CONFIRM'),
      }
);
const canEnableHybrid = computed(
  () => qr.value.state === HYBRID_QR_STATE.CONNECTED
);

const fail = e => {
  error.value =
    e?.response?.data?.error || t('INBOX_MGMT.EDIT.HYBRID_QR.ERROR');
};

const cacheState = async data => {
  if (!data?.connection || data.connection === 'not_configured') return;
  const { qr_data_url: _qr, pairing_code: _code, ...publicState } = data;
  await store.dispatch('inboxes/updateAttendanceConnection', {
    id: props.inbox.id,
    attendanceConnection: publicState,
  });
};

const verify = async () => {
  try {
    const { data } = await InboxesAPI.attendanceConnectionStatus(
      props.inbox.id
    );
    if (!disposed && !data.qr_channel_id) await cacheState(data);
  } catch (e) {
    if (!disposed) fail(e);
  }
};

// The connection already on the number is only an extra: when it cannot be listed, the other ways remain.
const loadNativeInstances = async () => {
  try {
    const { data } = await InboxesAPI.nativeInstances(props.inbox.id);
    return data.instances || [];
  } catch {
    return [];
  }
};

const loadOptions = async () => {
  try {
    const [{ data }, instances] = await Promise.all([
      InboxesAPI.qrLinkCandidates(props.inbox.id),
      loadNativeInstances(),
    ]);
    if (disposed) return;
    managedAvailable.value = data.managed_available === true;
    candidates.value = data.candidates || [];
    nativeInstances.value = instances;
    selectedNative.value = 0;
    showAdvanced.value = false;
    selectedCandidate.value = candidates.value[0]?.qr_channel_id || '';
    selectedOption.value = options.value[0].id;
  } catch {
    // Without the list, the manual form is still there.
  }
};

onMounted(() => {
  if (qr.value.source !== HYBRID_QR_SOURCE.LINKED) verify();
  if (!qr.value.source) loadOptions();
});
onBeforeUnmount(() => {
  disposed = true;
});

// Runs one request of the card and reports its failure on the card itself.
const run = async request => {
  busy.value = true;
  error.value = '';
  try {
    await request();
  } catch (e) {
    fail(e);
  } finally {
    busy.value = false;
  }
};

const openPairing = async () => {
  const data = await store.dispatch(
    'inboxes/setupGroupTransport',
    props.inbox.id
  );
  await cacheState(data);
  changingMethod.value = false;
  showLinkDeviceModal.value = true;
};

const connectManaged = () =>
  run(async () => {
    const { data } = await InboxesAPI.setupAttendanceConnection(
      props.inbox.id,
      { managed: true }
    );
    await store.dispatch('inboxes/refreshInbox', props.inbox.id);
    await store.dispatch('inboxes/updateGroupTransport', {
      id: props.inbox.id,
      groupTransport: data,
    });
    changingMethod.value = false;
    showLinkDeviceModal.value = true;
  });

const connectManual = () =>
  run(async () => {
    const { data } = await InboxesAPI.setupAttendanceConnection(
      props.inbox.id,
      {
        base_url: baseUrl.value.trim(),
        ...(token.value ? { instance_token: token.value.trim() } : {}),
      }
    );
    token.value = '';
    await store.dispatch('inboxes/refreshInbox', props.inbox.id);
    await store.dispatch('inboxes/updateGroupTransport', {
      id: props.inbox.id,
      groupTransport: data,
    });
    changingMethod.value = false;
    showLinkDeviceModal.value = true;
  });

const linkExisting = () =>
  run(async () => {
    await InboxesAPI.linkQrChannel(props.inbox.id, selectedCandidate.value);
    await store.dispatch('inboxes/refreshInbox', props.inbox.id);
    changingMethod.value = false;
  });

const linkNative = () =>
  run(async () => {
    const { provider_id: providerId, instance_name: instanceName } =
      nativeInstance.value;
    await InboxesAPI.linkNativeInstance(
      props.inbox.id,
      providerId,
      instanceName
    );
    await store.dispatch('inboxes/refreshInbox', props.inbox.id);
    changingMethod.value = false;
  });

const unlinkNative = () =>
  run(async () => {
    await InboxesAPI.unlinkNativeInstance(props.inbox.id);
    await store.dispatch('inboxes/refreshInbox', props.inbox.id);
    await loadOptions();
  });

const unlink = () =>
  run(async () => {
    await InboxesAPI.unlinkQrChannel(props.inbox.id);
    await store.dispatch('inboxes/refreshInbox', props.inbox.id);
    await loadOptions();
  });

const connectSelected = () =>
  ({
    [OPTION.NATIVE]: linkNative,
    [OPTION.MANAGED]: connectManaged,
    [OPTION.LINKED]: linkExisting,
    [OPTION.MANUAL]: connectManual,
  })[selectedOption.value]();

const canConnectSelected = computed(() => {
  if (selectedOption.value === OPTION.NATIVE) return !!nativeInstance.value;
  if (selectedOption.value === OPTION.LINKED) return !!selectedCandidate.value;
  if (selectedOption.value === OPTION.MANUAL) return !!baseUrl.value.trim();
  return true;
});

const connectLabel = computed(
  () =>
    ({
      [OPTION.NATIVE]: t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.USE_NATIVE'),
      [OPTION.LINKED]: t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.LINK'),
    })[selectedOption.value] || t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.CONNECT')
);

// Hiding the advanced form must not leave it as the selected way.
const toggleAdvanced = () => {
  showAdvanced.value = !showAdvanced.value;
  if (!showAdvanced.value && selectedOption.value === OPTION.MANUAL) {
    selectedOption.value = options.value[0].id;
  }
};

const cancelPairing = () =>
  run(async () => {
    const { data } = await InboxesAPI.disconnectAttendanceConnection(
      props.inbox.id
    );
    await cacheState(data);
  });

const disconnect = () => {
  confirmDialog.value?.close();
  return isNative.value ? unlinkNative() : cancelPairing();
};

const openQrInbox = () =>
  router.push({
    name: 'settings_inbox_show',
    params: {
      accountId: accountId.value,
      inboxId: qr.value.qrInboxId,
      tab: 'configuration',
    },
  });

const candidateLabel = candidate =>
  `${candidate.name} · ${
    candidate.connection === 'open'
      ? t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_LINKED.OPEN')
      : t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_LINKED.CLOSED')
  }`;

const saveHybridAttendance = async () => {
  isSavingHybridAttendance.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      channel: {
        provider_config: {
          ...props.inbox.provider_config,
          hybrid_attendance_enabled: hybridAttendanceEnabled.value,
        },
      },
    });
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch {
    hybridAttendanceEnabled.value =
      props.inbox.hybrid_attendance_enabled === true;
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSavingHybridAttendance.value = false;
  }
};
</script>

<template>
  <div>
    <WhatsappLinkDeviceModal
      v-if="showLinkDeviceModal"
      :show="showLinkDeviceModal"
      :on-close="() => (showLinkDeviceModal = false)"
      :inbox="inbox"
      group-transport
    />
    <Dialog
      ref="confirmDialog"
      type="alert"
      :title="confirmText.title"
      :description="confirmText.description"
      :confirm-button-label="confirmText.confirm"
      @confirm="disconnect"
    />
    <SettingsSection
      :title="t('INBOX_MGMT.EDIT.HYBRID_QR.TITLE')"
      :sub-title="t('INBOX_MGMT.EDIT.HYBRID_QR.DESCRIPTION')"
    >
      <div class="flex flex-col gap-4">
        <p class="m-0 text-xs text-n-slate-11">
          {{
            t('INBOX_MGMT.EDIT.HYBRID_QR.OFFICIAL_LINE', {
              provider: officialProvider,
            })
          }}
        </p>

        <HybridQrStatusBadge
          :state="qr.state"
          :phone-last4="qr.phoneLast4"
          :checked-at="qr.checkedAt"
        />
        <p v-if="qr.error" role="alert" class="m-0 text-sm text-n-ruby-11">
          {{ qr.error }}
        </p>

        <p v-if="qr.source === 'linked'" class="m-0 text-sm text-n-slate-12">
          {{
            t('INBOX_MGMT.EDIT.HYBRID_QR.LINKED_TO', { name: qr.qrInboxName })
          }}
          <span class="block text-n-slate-11">
            {{ t('INBOX_MGMT.EDIT.HYBRID_QR.LINKED_NOTE') }}
          </span>
        </p>

        <p v-if="isNative" class="m-0 text-sm text-n-slate-12">
          {{
            t('INBOX_MGMT.EDIT.HYBRID_QR.LINKED_NATIVE', {
              provider: qr.providerName,
            })
          }}
          <span class="block text-n-slate-11">
            {{ t('INBOX_MGMT.EDIT.HYBRID_QR.LINKED_NATIVE_NOTE') }}
          </span>
        </p>

        <div v-if="showSelector" class="flex flex-col gap-3">
          <h4 class="m-0 text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.EDIT.HYBRID_QR.HOW_TO_CONNECT') }}
          </h4>
          <RadioCard
            v-for="option in options"
            :id="`hybrid-qr-${option.id}`"
            :key="option.id"
            name="hybrid-qr-option"
            :label="option.label"
            :description="option.description"
            :is-active="selectedOption === option.id"
            @select="selectedOption = option.id"
          >
            <template v-if="selectedOption === option.id">
              <label
                v-if="option.id === 'linked'"
                class="flex w-full flex-col gap-1 text-sm text-n-slate-12"
              >
                {{ t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_LINKED.SELECT') }}
                <select
                  v-model="selectedCandidate"
                  :disabled="busy"
                  class="mb-0 w-full"
                >
                  <option
                    v-for="candidate in candidates"
                    :key="candidate.qr_channel_id"
                    :value="candidate.qr_channel_id"
                  >
                    {{ candidateLabel(candidate) }}
                  </option>
                </select>
              </label>
              <template v-else-if="option.id === 'native'">
                <label
                  v-if="nativeInstances.length > 1"
                  class="flex w-full flex-col gap-1 text-sm text-n-slate-12"
                >
                  {{ t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_NATIVE.SELECT') }}
                  <select
                    v-model="selectedNative"
                    :disabled="busy"
                    class="mb-0 w-full"
                  >
                    <option
                      v-for="(instance, index) in nativeInstances"
                      :key="`${instance.provider_id}-${instance.instance_name}`"
                      :value="index"
                    >
                      {{ nativeLabel(instance) }}
                    </option>
                  </select>
                </label>
                <p class="m-0 text-xs text-n-slate-11">
                  {{
                    t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_NATIVE.PROVIDER', {
                      provider: nativeInstance.provider_name,
                    })
                  }}
                </p>
                <p
                  v-if="nativeInstance.other_webhook"
                  role="alert"
                  class="m-0 text-xs text-n-amber-11"
                >
                  {{
                    t('INBOX_MGMT.EDIT.HYBRID_QR.OPTION_NATIVE.WEBHOOK_WARNING')
                  }}
                </p>
              </template>
              <HybridQrManualForm
                v-else-if="option.id === 'manual'"
                v-model:base-url="baseUrl"
                v-model:token="token"
                class="w-full"
                :busy="busy"
              />
            </template>
          </RadioCard>
        </div>

        <button
          v-if="showSelector && !manualOnly"
          type="button"
          class="self-start text-sm text-n-brand underline"
          @click="toggleAdvanced"
        >
          {{
            advancedVisible
              ? t('INBOX_MGMT.EDIT.HYBRID_QR.ADVANCED.HIDE')
              : t('INBOX_MGMT.EDIT.HYBRID_QR.ADVANCED.SHOW')
          }}
        </button>

        <p v-if="error" role="alert" class="m-0 text-sm text-n-ruby-11">
          {{ error }}
        </p>

        <div class="flex flex-wrap gap-2">
          <template v-if="showSelector">
            <NextButton
              :disabled="busy || !canConnectSelected"
              :is-loading="busy"
              @click="connectSelected"
            >
              {{ connectLabel }}
            </NextButton>
            <NextButton
              v-if="changingMethod"
              variant="outline"
              :disabled="busy"
              @click="changingMethod = false"
            >
              {{ t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.CANCEL') }}
            </NextButton>
          </template>
          <template v-else-if="isManagedOrManual">
            <template v-if="qr.state === 'waiting'">
              <NextButton
                :disabled="busy"
                :is-loading="busy"
                @click="run(openPairing)"
              >
                {{ t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.NEW_QR') }}
              </NextButton>
              <NextButton
                variant="outline"
                :disabled="busy"
                @click="cancelPairing"
              >
                {{ t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.CANCEL') }}
              </NextButton>
            </template>
            <template v-else-if="qr.state === 'connected'">
              <NextButton
                variant="outline"
                :disabled="busy"
                @click="run(verify)"
              >
                {{ t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.VERIFY') }}
              </NextButton>
              <NextButton
                variant="outline"
                color="ruby"
                :disabled="busy"
                @click="confirmDialog?.open()"
              >
                {{
                  isNative
                    ? t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.UNLINK')
                    : t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.DISCONNECT')
                }}
              </NextButton>
            </template>
            <template v-else>
              <NextButton
                :disabled="busy"
                :is-loading="busy"
                @click="run(openPairing)"
              >
                {{
                  qr.state === 'disconnected'
                    ? t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.RECONNECT')
                    : t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.CONNECT')
                }}
              </NextButton>
              <NextButton
                variant="outline"
                :disabled="busy"
                @click="changingMethod = true"
              >
                {{ t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.CHANGE_METHOD') }}
              </NextButton>
            </template>
          </template>
          <template v-else>
            <NextButton
              v-if="qr.state !== 'connected'"
              :disabled="busy"
              @click="openQrInbox"
            >
              {{ t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.OPEN_QR_INBOX') }}
            </NextButton>
            <NextButton variant="outline" :disabled="busy" @click="unlink">
              {{ t('INBOX_MGMT.EDIT.HYBRID_QR.ACTIONS.UNLINK') }}
            </NextButton>
          </template>
        </div>

        <div class="flex items-start gap-3 border-t border-n-weak pt-4">
          <Switch
            id="hybrid-attendance-enabled"
            v-model="hybridAttendanceEnabled"
            :disabled="
              isSavingHybridAttendance ||
              (!canEnableHybrid && !hybridAttendanceEnabled)
            "
            @change="saveHybridAttendance"
          />
          <label
            for="hybrid-attendance-enabled"
            class="text-sm text-n-slate-12"
          >
            {{ t('INBOX_MGMT.EDIT.HYBRID_QR.TOGGLE.LABEL') }}
            <span class="block text-n-slate-11">
              {{ t('INBOX_MGMT.EDIT.HYBRID_QR.TOGGLE.DESCRIPTION') }}
            </span>
            <span
              v-if="!canEnableHybrid && !hybridAttendanceEnabled"
              class="block text-n-amber-11"
            >
              {{ t('INBOX_MGMT.EDIT.HYBRID_QR.TOGGLE.BLOCKED') }}
            </span>
          </label>
        </div>
        <p class="m-0 text-xs text-n-slate-10">
          {{ t('INBOX_MGMT.EDIT.HYBRID_QR.BETA_NOTE') }}
        </p>
      </div>
    </SettingsSection>
  </div>
</template>
